import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { useLocation } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';
import { useUIStore } from '../hooks/useUIStore';
import {
  Download,
  RefreshCw,
  Plus,
} from 'lucide-react';
import {
  ScheduleCalendar,
  type CalendarViewType,
  type ScheduleCalendarHandle,
  computeScheduleScopeCounts,
  getWarehouseTodayIso,
  getWarehouseWeekMondayIso,
  getWarehouseMonthStartIso,
} from '../components/schedule/ScheduleCalendar';
import { ScheduleBookingSlideOver } from '../components/schedule/ScheduleBookingSlideOver';
import { Appointment } from '../types/appointments';
import { ApprovalQueueList } from '../components/appointments/ApprovalQueueList';
import type { BlackoutWindow } from '../components/docks/BlackoutList';
import { BlackoutCreateModal } from '../components/docks/BlackoutCreateModal';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../utils/timezoneDisplay';
import { ShieldOff } from 'lucide-react';

import './Dashboard.css';

type FilterType = 'today' | '7days' | 'month';

const fetcher = async ([url, token]: [string, string]) => {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
};

/** ---------------------------
 * HOISTED HELPERS (avoid TDZ)
 * --------------------------*/

// Status normalization helper (prevents case-sensitive bugs)
function normalizeStatus(status: string | undefined) {
  return (status ?? '').toLowerCase();
}

// Proper "time ago" helper (for Last updated)
function getTimeAgo(timestampMs: number) {
  const now = Date.now();
  const diffMs = Math.max(0, now - timestampMs);
  const mins = Math.floor(diffMs / 60000);

  if (mins <= 0) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Preferences (simplified — time scope only)
const STORAGE_KEY = 'bw_schedule_prefs_v3';

function loadSavedScope(): FilterType {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return 'today';
    const parsed = JSON.parse(stored);
    const valid: FilterType[] = ['today', '7days', 'month'];
    return valid.includes(parsed?.scope) ? parsed.scope : 'today';
  } catch {
    return 'today';
  }
}

function saveScope(scope: FilterType) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ scope }));
  } catch {
    // noop
  }
}

export function AppointmentsTab() {
  const { token, user } = useAuthStore();
  const [, setLocation] = useLocation();
  const isWarehouseUser = Boolean(
    user?.role && ['warehouse_manager', 'warehouse_staff', 'admin'].includes(user.role)
  );
  const { openAppointmentDetail, registerActionSuccessListener, unregisterActionSuccessListener } =
    useUIStore();

  useEffect(() => {
    if (user?.role === 'carrier') {
      setLocation('/carrier-dashboard');
    }
  }, [user?.role, setLocation]);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [showBlackoutModal, setShowBlackoutModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState(''); // Never persisted
  const [activeFilter, setActiveFilter] = useState<FilterType>(loadSavedScope());
  const calendarRef = useRef<ScheduleCalendarHandle>(null);

  const [actionError, setActionError] = useState<string | null>(null);

  const warehouseTimeZone = user?.timeZone || 'America/Los_Angeles';

  const [selectedDayIso, setSelectedDayIso] = useState<string>(() =>
    getWarehouseTodayIso(warehouseTimeZone)
  );
  const [selectedWeekMondayIso, setSelectedWeekMondayIso] = useState<string>(() =>
    getWarehouseWeekMondayIso(warehouseTimeZone)
  );
  const [selectedMonthStartIso, setSelectedMonthStartIso] = useState<string>(() =>
    getWarehouseMonthStartIso(warehouseTimeZone)
  );

  const prevTzRef = useRef<string | undefined>(user?.timeZone);
  useEffect(() => {
    if (user?.timeZone && user.timeZone !== prevTzRef.current) {
      prevTzRef.current = user.timeZone;
      setSelectedDayIso(getWarehouseTodayIso(user.timeZone));
      setSelectedWeekMondayIso(getWarehouseWeekMondayIso(user.timeZone));
      setSelectedMonthStartIso(getWarehouseMonthStartIso(user.timeZone));
    }
  }, [user?.timeZone]);

  // Persist scope to localStorage when it changes
  useEffect(() => {
    saveScope(activeFilter);
  }, [activeFilter]);

  // Map time-scope to FullCalendar view type
  const calendarView: CalendarViewType = useMemo(() => {
    switch (activeFilter) {
      case 'today': return 'timeGridDay';
      case '7days': return 'timeGridWeek';
      case 'month': return 'dayGridMonth';
      default: return 'dayGridMonth';
    }
  }, [activeFilter]);

  // Batch 11: Real-time "Last updated" & 60s ticker
  const [minutelyTick, setMinutelyTick] = useState(Date.now());
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setMinutelyTick(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Always fetch 'all' — time scoping is done client-side
  const apiFilter = 'all';

  const { data, error, isLoading, mutate, isValidating } = useSWR<{ appointments: Appointment[] }>(
    token && isWarehouseUser ? [`/api/appointments/universal?filter=${apiFilter}`, token] : null,
    fetcher,
    { refreshInterval: 30000 }
  );

  // Dock count — lightweight fetch for capacity awareness
  const { data: docksData } = useSWR<{ docks: any[] }>(
    token && isWarehouseUser && user?.warehouseId ? [`/api/warehouses/${user.warehouseId}/docks`, token] : null,
    fetcher,
    { refreshInterval: 300000 } // 5 min — rarely changes
  );
  const dockCount = docksData?.docks?.length || 0;

  // V1.3C: Blackouts for schedule timeline
  const { data: blackoutData } = useSWR<{ blackouts: BlackoutWindow[] }>(
    token && isWarehouseUser && user?.warehouseId ? ['/api/docks/blackouts?from=' + new Date(new Date().setHours(0,0,0,0)).toISOString() + '&to=' + new Date(new Date().setHours(23,59,59,999)).toISOString(), token] : null,
    fetcher,
    { refreshInterval: 30000 }
  );

  useEffect(() => {
    if (data) setLastUpdated(Date.now());
  }, [data]);

  // Register instant-refresh: fired after any modal action (dock assign, approve, etc.)
  const refreshSchedule = useCallback(() => mutate(), [mutate]);
  useEffect(() => {
    registerActionSuccessListener(refreshSchedule);
    return () => unregisterActionSuccessListener(refreshSchedule);
  }, [refreshSchedule, registerActionSuccessListener, unregisterActionSuccessListener]);

  const appointments: Appointment[] = data?.appointments || [];

  // --- BASE SEARCH FILTER (used for KPIs + then for scoped list) ---
  const searchFilteredAppointments = useMemo(() => {
    let result = appointments;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (a) =>
          a.customerPoNumber?.toLowerCase().includes(term) ||
          a.carrierName?.toLowerCase().includes(term) ||
          a.warehouseName?.toLowerCase().includes(term) ||
          a.driverName?.toLowerCase().includes(term) ||
          a.loadNumber?.toLowerCase().includes(term)
      );
    }

    return result;
  }, [appointments, searchTerm]);

  // Filtered = scoped (search only — calendar handles its own date range)
  const filteredAppointments = searchFilteredAppointments;

  // KPI alert chips (computed from search-filtered full dataset)
  const kpis = useMemo(() => {
    const now = new Date(minutelyTick);

    const late = searchFilteredAppointments.filter((a) => {
      const status = normalizeStatus(a.status);
      if (status === 'completed' || status === 'checked_in' || status === 'in_progress') return false;
      const scheduled = new Date(a.startAt);
      const diff = now.getTime() - scheduled.getTime();
      return diff > 0 && diff < 24 * 60 * 60 * 1000;
    }).length;

    const unassignedDoor = searchFilteredAppointments.filter((a) => {
      const status = normalizeStatus(a.status);
      return status !== 'completed' && 'dock_id' in a && !a.dock_id;
    }).length;

    return { late, unassignedDoor };
  }, [searchFilteredAppointments, minutelyTick]);

  // Scope counts for tab badges — uses the exact same warehouse-timezone date ranges as the calendar views
  const scopeCounts = useMemo(
    () =>
      computeScheduleScopeCounts(searchFilteredAppointments, {
        warehouseTimeZone,
        selectedDayIso,
        selectedWeekMondayIso,
        selectedMonthStartIso,
      }),
    [searchFilteredAppointments, warehouseTimeZone, selectedDayIso, selectedWeekMondayIso, selectedMonthStartIso]
  );

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['PO Number', 'Carrier', 'Warehouse', 'Status', 'Scheduled', 'Driver', 'Check In', 'Check Out'];
    const rows = filteredAppointments.map((a) => [
      a.customerPoNumber || '',
      a.carrierName ? `${a.carrierName} (MC ${a.carrierMcNumber})` : (a.carrierMcNumber || ''),
      a.warehouseName || '',
      a.status || '',
      a.startAt ? formatDateInTimeZone(a.startAt, user?.timeZone, true) : '',
      a.driverName || '',
      a.checkInTime ? formatDateInTimeZone(a.checkInTime, user?.timeZone, true) : '',
      a.checkOutTime ? formatDateInTimeZone(a.checkOutTime, user?.timeZone, true) : '',
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `appointments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAppointmentClick = (appt: Appointment) => {
    const storeAppt = {
      ...appt,
      po_number: appt.customerPoNumber,
      carrier_name: appt.carrierName,
      scheduled_time: appt.startAt,
    };
    openAppointmentDetail(storeAppt);
  };

  // "Next appointment" helper — Today view only
  const nextAppt = useMemo(() => {
    if (activeFilter !== 'today') return null;
    const now = new Date();
    const upcoming = searchFilteredAppointments
      .filter(a => {
        const s = (a.status || '').toLowerCase();
        if (s === 'completed' || s === 'cancelled') return false;
        return new Date(a.startAt) > now;
      })
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    if (upcoming.length === 0) return null;
    const appt = upcoming[0];
    const time = formatTimeInTimeZone(appt.startAt, user?.timeZone);
    const dockId = appt.dockId || appt.dock_id;
    const dock = dockId ? `Dock ${String(dockId).length <= 6 ? dockId : '\u2713'}` : null;
    return { time, dock, carrier: appt.carrierName };
  }, [activeFilter, searchFilteredAppointments, user?.timeZone]);

  // Pending approval requests — filtered from universal data
  const pendingAppointments = useMemo(() =>
    searchFilteredAppointments.filter(a => normalizeStatus(a.status) === 'pending'),
    [searchFilteredAppointments]
  );

  // Declined and carrier-cancelled history — preserved for audit visibility
  const cancelledAppointments = useMemo(() =>
    searchFilteredAppointments
      .filter(a => normalizeStatus(a.status) === 'cancelled')
      .sort((a, b) => {
        const aTime = new Date((a as any).updatedAt || a.requestedAt || a.startAt || 0).getTime();
        const bTime = new Date((b as any).updatedAt || b.requestedAt || b.startAt || 0).getTime();
        return bTime - aTime;
      }),
    [searchFilteredAppointments]
  );

  if (user?.role === 'carrier') {
    return null;
  }

  if (isLoading) {
    return (
      <div className="warehouse-dashboard p-8">
        <div className="h-10 bg-slate-200 dark:bg-slate-800 rounded-lg mb-4 animate-pulse" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-slate-100 dark:bg-slate-900 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) return <div className="card text-red-600 dark:text-red-400">Error loading appointments</div>;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Schedule workspace — shrinks when booking panel opens */}
      <div className={`flex-1 min-w-0 overflow-y-auto transition-all ${bookingOpen ? '' : ''}`}>
          <div className="warehouse-dashboard mob-header-pt px-3 sm:px-6 pb-8">
            <header className="mb-4 pl-14 pt-2 md:pl-0 md:pt-0 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="h2">Schedule</h1>
                <p className="text-muted">View and manage all your scheduled appointments</p>
                {user?.timeZone && (
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 font-medium">
                    All times shown in warehouse local time ({user.timeZone})
                  </p>
                )}
              </div>

            <div className="flex items-center gap-2 mb-1">
              {lastUpdated && (
                <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline-block">
                  Last updated: {getTimeAgo(lastUpdated)}
                </span>
              )}
              {isValidating && (
                <span className="text-xs text-blue-600 dark:text-blue-400 animate-pulse">Refreshing...</span>
              )}
              <button
                onClick={() => mutate()}
                disabled={isLoading || isValidating}
                className="btn btn-ghost btn-sm p-1.5 h-auto text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                title="Refresh now"
              >
            <RefreshCw className={`w-3.5 h-3.5 ${isValidating ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {/* Alert badges — only shown when exceptions exist */}
      {(kpis.late > 0 || kpis.unassignedDoor > 0) && (
        <div className="flex gap-2 mb-4 flex-wrap text-xs">
          {kpis.late > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800/50">
              <span className="font-medium">Late</span>
              <span className="font-bold tabular-nums">{kpis.late}</span>
            </span>
          )}
          {kpis.unassignedDoor > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50">
              <span className="font-medium">No Door</span>
              <span className="font-bold tabular-nums">{kpis.unassignedDoor}</span>
            </span>
          )}
        </div>
      )}

      {/* Toolbar: Search | Scope selector | Export */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
          <input
            type="text"
            placeholder={"\uD83D\uDD0D Search PO, load, carrier, driver..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="field w-full max-w-xs md:w-72"
          />

          {/* Time-scope selector — segmented control */}
          <div className="flex gap-0 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
            {(['today', '7days', 'month'] as const).map((scope) => (
              <button
                key={scope}
                onClick={() => setActiveFilter(scope)}
                className={`px-4 py-1.5 text-xs sm:text-sm rounded-md transition-all ${
                  activeFilter === scope
                    ? 'bg-blue-600 text-white font-semibold shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                {scope === 'today'
                  ? `Today (${scopeCounts.today})`
                  : scope === '7days'
                    ? `7 Days (${scopeCounts.days7})`
                    : `Month (${scopeCounts.month})`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Error banner - dismissible */}
          {actionError && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-300 dark:border-red-700 rounded-lg p-3 flex items-start justify-between gap-2">
              <p className="text-red-800 dark:text-red-200 text-sm flex-1">{actionError}</p>
              <button
                onClick={() => setActionError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          <button onClick={exportToCSV} className="btn btn-success flex items-center gap-2 shrink-0">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
          {(user?.role === 'warehouse_manager' || user?.role === 'admin') && (
            <button
              onClick={() => setShowBlackoutModal(true)}
              className="flex items-center gap-2 shrink-0 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
            >
              <ShieldOff className="w-4 h-4" />
              <span className="hidden sm:inline">Block Time</span>
            </button>
          )}
          <button
            onClick={() => setBookingOpen(true)}
            className="flex items-center gap-2 shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Schedule Appointment</span>
          </button>
        </div>
      </div>

      {/* Next appointment marker — Today view only */}
      {nextAppt && (
        <div className="flex items-center gap-2 mb-3 px-1 text-xs">
          <span className="font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Next →</span>
          <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">{nextAppt.time}</span>
          {nextAppt.dock && (
            <span className="font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded text-[11px]">
              {nextAppt.dock}
            </span>
          )}
          {nextAppt.carrier && (
            <span className="text-slate-500 dark:text-slate-400 truncate max-w-[200px]">{nextAppt.carrier}</span>
          )}
        </div>
      )}

      {/* Approval Queue — shared decision surface */}
      <ApprovalQueueList
        id="schedule-approval-queue"
        requests={pendingAppointments}
        warehouseTimeZone={user?.timeZone}
        onCardClick={(req) => handleAppointmentClick(req)}
        onApprove={(req) => handleAppointmentClick(req)}
        onRevise={(req) => handleAppointmentClick(req)}
        onDecline={(req) => handleAppointmentClick(req)}
      />

      {/* Calendar surface */}
      <ScheduleCalendar
        ref={calendarRef}
        appointments={filteredAppointments}
        warehouseTimeZone={user?.timeZone}
        onEventClick={handleAppointmentClick}
        dockCount={dockCount}
        initialView={calendarView}
        docks={docksData?.docks || []}
        blackouts={blackoutData?.blackouts}
        selectedDayIso={selectedDayIso}
        onDayChange={setSelectedDayIso}
        selectedWeekMondayIso={selectedWeekMondayIso}
        onWeekChange={setSelectedWeekMondayIso}
        selectedMonthStartIso={selectedMonthStartIso}
        onMonthChange={setSelectedMonthStartIso}
      />

      {cancelledAppointments.length > 0 && (
        <div className="mt-6 bg-white dark:bg-slate-900 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider">
                Declined & Cancelled History ({cancelledAppointments.length})
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Audit trail of warehouse-declined requests and carrier-cancelled appointments
              </p>
            </div>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {cancelledAppointments.slice(0, 20).map((appt) => {
              const notesText = String((appt as any).notes || '').trim();
              const lowerNotes = notesText.toLowerCase();
              const isCarrierCancelled = lowerNotes.includes('cancelled by carrier');
              const isWarehouseDeclined = !isCarrierCancelled && lowerNotes.includes('declined');
              const badgeLabel = isCarrierCancelled
                ? 'Cancelled by Carrier'
                : isWarehouseDeclined
                  ? 'Declined by Warehouse'
                  : 'Cancelled by Warehouse';
              const badgeClasses = isCarrierCancelled
                ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 border-rose-200 dark:border-rose-800/50'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800/50';
              const approvedAtVal = (appt as any).approvedAt || (appt as any).approved_at;

              return (
                <div
                  key={appt.id}
                  onClick={() => handleAppointmentClick(appt)}
                  className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 px-2 rounded-lg transition-colors"
                >
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-100">
                        PO #{appt.customerPoNumber || 'N/A'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${badgeClasses}`}>
                        {badgeLabel}
                      </span>
                      {approvedAtVal && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50">
                          Originally Approved ({formatDateInTimeZone(approvedAtVal, user?.timeZone, true)})
                        </span>
                      )}
                      <span className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                        {appt.carrierName || 'Unknown Carrier'}
                        {appt.carrierMcNumber ? ` (MC#${appt.carrierMcNumber})` : ''}
                      </span>
                    </div>
                    {notesText && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">
                        {notesText}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 shrink-0 sm:text-right">
                    <div>
                      Requested: {appt.startAt ? formatDateInTimeZone(appt.startAt, user?.timeZone, true) : 'N/A'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 text-muted text-sm">
        Showing {filteredAppointments.filter(a => normalizeStatus(a.status) !== 'cancelled').length} active scheduled appointments
        {cancelledAppointments.length > 0 ? ` (${cancelledAppointments.length} declined/cancelled in history)` : ''}
      </div>
        </div>
      </div>

      {/* Docked booking panel — renders inline, shrinks calendar via flex */}
      {bookingOpen && (
        <ScheduleBookingSlideOver
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          onSuccess={() => {
            setBookingOpen(false);
            mutate();
          }}
        />
      )}

      {/* V1.3B: Blackout Create Modal */}
      <BlackoutCreateModal
          open={showBlackoutModal}
          onClose={() => setShowBlackoutModal(false)}
          onCreated={() => {
              setShowBlackoutModal(false);
              mutate();
          }}
          docks={(docksData?.docks || []).map(d => ({ id: d.id, dockNumber: d.dockNumber }))}
      />
    </div>
  );
}
